import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../../lib/utils';

/** The leading MARK of a record: one bordered tile holding an icon, a glyph or a monogram, drawn beside a
 *  card's title. It had been copied by hand into ten cards across the app and the plugins — the same greys,
 *  the same radius, the same box restated at every call site — which is the one thing a shared component
 *  exists to stop: a design that moves the muted step, or a card that wants its mark a shade warmer, was an
 *  edit in ten files.
 *
 *  The three steps are the ones the copies already used, each on the spacing scale, so every existing card
 *  renders exactly as it did: `sm` is the 36px tile a register row takes, `default` the 40px one a section
 *  card stands a category icon in, `large` the 44px identity tile of a project card (the one step that also
 *  pairs with the larger radius, as its copies did). Two tones: `muted` for an identity, `accent` for an
 *  entry the reader made rather than one that shipped. `children` is the mark itself (an icon, a
 *  `ProjectIcon`, a letter): this component owns the BOX and never the glyph, and the remaining props land
 *  on the tile so a caller keeps its `aria-hidden` and its test marker.
 *
 *  CORE ONLY for now. It is deliberately not exposed through `window.ElowenUiRuntime`: doing so would move
 *  `PLUGIN_UI_API_VERSION` and force every plugin in the registry to ship a matching release, so the plugin
 *  copies stay as they are until the next UI API cut. */
export function IconTile({ children, size = 'sm', tone = 'muted', className, ...rest }: ComponentProps<'span'> & {
  children: ReactNode;
  size?: 'sm' | 'default' | 'large';
  tone?: 'muted' | 'accent';
}) {
  return (
    <span
      data-slot="icon-tile"
      data-tone={tone}
      className={cn(
        'flex shrink-0 items-center justify-center border',
        { sm: 'size-9 rounded-lg', default: 'size-10 rounded-lg', large: 'size-11 rounded-xl' }[size],
        tone === 'accent'
          ? 'border-primary/25 bg-primary/10 text-primary'
          : 'border-border bg-muted/60 text-muted-foreground',
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}
